import {
  collection,
  doc,
  addDoc,
  updateDoc,
  deleteDoc,
  getDocs,
  getDoc,
  query,
  where,
  orderBy,
  writeBatch
} from 'firebase/firestore';
import { db } from './firebase';
import { Review } from '../types/book';
import { authService } from './authService';

export interface ReviewFormData {
  rating: number;
  comment: string;
}

export const reviewService = {
  // Create a new review
  async createReview(bookId: string, userId: string, reviewData: ReviewFormData): Promise<string> {
    // Get user data for display name
    const userData = await authService.getUserData(userId);
    
    const review = {
      bookId,
      userId,
      rating: reviewData.rating,
      comment: reviewData.comment,
      timestamp: new Date(),
      userDisplayName: userData.displayName
    };

    const docRef = await addDoc(collection(db, 'reviews'), review);
    
    // Update book's average rating
    await this.updateBookAverageRating(bookId);
    
    return docRef.id;
  },

  // Get all reviews for a book
  async getReviewsByBookId(bookId: string): Promise<Review[]> {
    const q = query(
      collection(db, 'reviews'),
      where('bookId', '==', bookId),
      orderBy('timestamp', 'desc')
    );
    
    const querySnapshot = await getDocs(q);
    
    return querySnapshot.docs.map(doc => ({
      id: doc.id,
      ...doc.data(),
      timestamp: doc.data().timestamp?.toDate() || new Date()
    })) as Review[];
  },

  // Get a single review by ID
  async getReviewById(id: string): Promise<Review | null> {
    const docRef = doc(db, 'reviews', id);
    const docSnap = await getDoc(docRef);
    
    if (docSnap.exists()) {
      const data = docSnap.data();
      return {
        id: docSnap.id,
        ...data,
        timestamp: data.timestamp?.toDate() || new Date()
      } as Review;
    }
    
    return null;
  },

  // Update a review
  async updateReview(id: string, reviewData: Partial<ReviewFormData>): Promise<void> {
    const docRef = doc(db, 'reviews', id);
    const updateData = {
      ...reviewData,
      timestamp: new Date() // Update timestamp
    };
    
    await updateDoc(docRef, updateData);
    
    // Get the review to update book's average rating
    const review = await this.getReviewById(id);
    if (review) {
      await this.updateBookAverageRating(review.bookId);
    }
  },

  // Delete a review
  async deleteReview(id: string): Promise<void> {
    // Get review data first to update book rating
    const review = await this.getReviewById(id);
    
    const docRef = doc(db, 'reviews', id);
    await deleteDoc(docRef);
    
    // Update book's average rating
    if (review) {
      await this.updateBookAverageRating(review.bookId);
    }
  },

  // Check if user has already reviewed a book
  async hasUserReviewed(bookId: string, userId: string): Promise<Review | null> {
    const q = query(
      collection(db, 'reviews'),
      where('bookId', '==', bookId),
      where('userId', '==', userId)
    );
    
    const querySnapshot = await getDocs(q);
    
    if (!querySnapshot.empty) {
      const doc = querySnapshot.docs[0];
      return {
        id: doc.id,
        ...doc.data(),
        timestamp: doc.data().timestamp?.toDate() || new Date()
      } as Review;
    }
    
    return null;
  },

  // Update book's average rating based on all reviews
  async updateBookAverageRating(bookId: string): Promise<void> {
    const reviews = await this.getReviewsByBookId(bookId);
    
    let averageRating = 0;
    if (reviews.length > 0) {
      const totalRating = reviews.reduce((sum, review) => sum + review.rating, 0);
      averageRating = totalRating / reviews.length;
    }
    
    // Update the book document
    const bookRef = doc(db, 'books', bookId);
    await updateDoc(bookRef, { averageRating });
  },

  // Get reviews by user
  async getReviewsByUserId(userId: string): Promise<Review[]> {
    const q = query(
      collection(db, 'reviews'),
      where('userId', '==', userId),
      orderBy('timestamp', 'desc')
    );
    
    const querySnapshot = await getDocs(q);
    
    return querySnapshot.docs.map(doc => ({
      id: doc.id,
      ...doc.data(),
      timestamp: doc.data().timestamp?.toDate() || new Date()
    })) as Review[];
  },

  // Delete all reviews for a book (used when deleting a book)
  async deleteReviewsByBookId(bookId: string): Promise<void> {
    const q = query(collection(db, 'reviews'), where('bookId', '==', bookId));
    const querySnapshot = await getDocs(q);
    
    const batch = writeBatch(db);
    querySnapshot.docs.forEach((doc) => {
      batch.delete(doc.ref);
    });
    
    await batch.commit();
  }
}; 